import { refreshAccessToken } from "@/features/auth/authApi"
import { tokenStorage } from "@/features/auth/tokenStorage"
import { parseSseNotification } from "@/entities/notification/notificationsApi"
import type { NotificationDto } from "@/entities/notification/types"
import { apiPaths } from "@/lib/api/backendUrls"
import { buildApiUrl } from "@/lib/api/config"
import type { TokenResponse } from "@/lib/api/http"

export type SseConnectionState = "connecting" | "open" | "closed"

type SetSession = (tokens: TokenResponse) => void

type StartOptions = {
  getAccessToken: () => string | null
  setSession: SetSession
  onNotification: (dto: NotificationDto, meta: { replay: boolean }) => void
  onStateChange?: (state: SseConnectionState) => void
  onFatalError?: (error: unknown) => void
}

type Controller = {
  stop: () => void
}

const MAX_RETRY_ATTEMPTS = 5
const BASE_RETRY_DELAY_MS = 1000
const MAX_RETRY_DELAY_MS = 30000
/** Окно после onopen, в которое события считаются replay-батчем (браузер прислал Last-Event-ID). */
const REPLAY_WINDOW_MS = 2500

/**
 * Открывает SSE-соединение на /api/app/notifications/stream. Токен передаётся в query
 * (EventSource не умеет Authorization). Браузер сам переподключается; при CLOSED мы
 * пробуем refresh и открываем заново — до MAX_RETRY_ATTEMPTS. `replay=true` для событий
 * в окне REPLAY_WINDOW_MS после каждого onopen, начиная со второго (первый onopen замыкает
 * пустая история — реального replay там нет, только новые события от этого момента).
 */
export const startNotificationsStream = ({
  getAccessToken,
  setSession,
  onNotification,
  onStateChange,
  onFatalError,
}: StartOptions): Controller => {
  let stopped = false
  let es: EventSource | null = null
  let attempts = 0
  let lastEventId: number | null = null
  let openCount = 0
  let replayUntil = 0
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null

  const setState = (state: SseConnectionState) => onStateChange?.(state)

  const cleanupSource = () => {
    if (es) {
      es.close()
      es = null
    }
  }

  const scheduleReconnect = () => {
    if (stopped) return
    attempts += 1
    if (attempts > MAX_RETRY_ATTEMPTS) {
      setState("closed")
      onFatalError?.(new Error("SSE reconnect limit reached"))
      return
    }
    const delay = Math.min(BASE_RETRY_DELAY_MS * 2 ** (attempts - 1), MAX_RETRY_DELAY_MS)
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null
      void connect()
    }, delay)
  }

  const tryRefreshToken = async (): Promise<boolean> => {
    const refresh = tokenStorage.getRefreshToken()
    if (!refresh) return false
    try {
      const tokens = await refreshAccessToken(refresh)
      setSession(tokens)
      return true
    } catch {
      return false
    }
  }

  const buildStreamUrl = (token: string) => {
    const params = new URLSearchParams({ access_token: token })
    if (lastEventId !== null) {
      params.set("lastEventId", String(lastEventId))
    }
    return `${buildApiUrl(apiPaths.notificationsStream)}?${params.toString()}`
  }

  const connect = async () => {
    if (stopped) return

    const token = getAccessToken()
    if (!token) {
      setState("closed")
      onFatalError?.(new Error("No access token"))
      return
    }

    setState("connecting")
    const source = new EventSource(buildStreamUrl(token), { withCredentials: false })
    es = source

    source.onopen = () => {
      attempts = 0
      openCount += 1
      /** На первом подключении replay пуст — это просто fresh stream, все события новые. */
      replayUntil = openCount > 1 ? Date.now() + REPLAY_WINDOW_MS : 0
      setState("open")
    }

    source.addEventListener("notification", (ev) => {
      const raw = (ev as MessageEvent).data
      const dto = parseSseNotification(typeof raw === "string" ? raw : "")
      if (!dto) return
      const idNum = Number((ev as MessageEvent).lastEventId)
      if (Number.isFinite(idNum) && idNum > 0) {
        lastEventId = lastEventId === null ? idNum : Math.max(lastEventId, idNum)
      } else if (Number.isFinite(dto.eventId)) {
        lastEventId = lastEventId === null ? dto.eventId : Math.max(lastEventId, dto.eventId)
      }
      const replay = Date.now() < replayUntil
      onNotification(dto, { replay })
    })

    source.onerror = async () => {
      if (stopped) return
      /** Временная ошибка при OPEN — доверяем встроенному reconnect браузера. */
      if (source.readyState !== EventSource.CLOSED) {
        return
      }
      cleanupSource()

      /** Скорее всего — протух токен. Пытаемся refresh до очередной попытки. */
      await tryRefreshToken()
      scheduleReconnect()
    }
  }

  void connect()

  return {
    stop() {
      stopped = true
      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer)
        reconnectTimer = null
      }
      cleanupSource()
      setState("closed")
    },
  }
}
